import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import {
  RULE_POST_ACTIVATE,
  RULE_POST_ANALYZE,
  RULE_POST_API_CALLS,
  RULE_POST_ARCHIVE,
  RULE_PUT,
  RULE_POST_DEACTIVATE,
  RULE_POST_ESTIMATE,
  RULE_GET,
  RULE_GET_API_CALL_DETAIL,
  RULE_GET_ESTIMATION,
  RULE_GET_EXECUTIONS,
  RULE_GET_HISTORY,
  RULE_GET_STATISTICS,
  RULE_GET_VOLUME,
  RULE_POST_REVIEW,
  RULE_POST_REJECT,
  RULE_PATCH,
} from '../endpoints/rule.endpoints';

import {
  RuleApiCallDetailResponse,
  RuleApiCallsResponse,
  RuleEstimationDetailResponse,
  RuleExecution,
  RuleFull,
  RuleHistory,
  RuleSettings,
} from 'src/app/connector-manager/connector.models';

import { IServerSideGetRowsRequest } from 'ag-grid-community';

import { Observable } from 'rxjs';

import { toQueryParams } from 'src/app/api/api-utils';

export interface RuleEstimationResponse {
  time_start: number;
  time_end: number;
  boolean_query: string;
  count: number;
  /** rule_estimation_log id of this call (WAP-1483). */
  fingerprint_id?: string;
  success?: boolean;
  error?: boolean;
  message?: string;
}

export interface RuleAnalyzeResponse {
  status: 'queued' | 'not_found';
  executionId?: string;
}

// TODO: We will pass typized objects to the services containing all inputs
@Injectable({
  providedIn: 'root',
})
export class RuleApiService {
  private readonly http = inject(HttpClient);

  getRule(ruleId: string): Observable<RuleFull> {
    return this.http.get<RuleFull>(RULE_GET(ruleId));
  }

  getRuleHistory(ruleId: string): Observable<RuleHistory[]> {
    return this.http.get<RuleHistory[]>(RULE_GET_HISTORY(ruleId));
  }

  getRuleVolumeData(ruleId: string): Observable<any> {
    return this.http.get(RULE_GET_VOLUME(ruleId));
  }

  getRuleStatistics(ruleId: string): Observable<any> {
    return this.http.get<any>(RULE_GET_STATISTICS(ruleId));
  }

  updateRule(
    ruleId: string,
    title: string,
    description: string,
    settings: RuleSettings,
    estimationFingerprintId?: string,
  ): Observable<RuleFull> {
    const params = {
      title,
      description,
      settings,
      ...(estimationFingerprintId
        ? { estimation_fingerprint_id: estimationFingerprintId }
        : {}),
    };

    return this.http.patch<any>(RULE_PATCH(ruleId), params);
  }

  reviewRule(ruleId: string): Observable<RuleFull> {
    return this.http.post<RuleFull>(RULE_POST_REVIEW(ruleId), {});
  }

  activateRule(ruleId: string): Observable<RuleFull> {
    return this.http.post<RuleFull>(RULE_POST_ACTIVATE(ruleId), {});
  }

  /** Reviewer sends an In Review rule back to Draft. */
  rejectRule(ruleId: string): Observable<RuleFull> {
    return this.http.post<RuleFull>(RULE_POST_REJECT(ruleId), {});
  }

  deactivateRule(ruleId: string): Observable<RuleFull> {
    return this.http.post<RuleFull>(RULE_POST_DEACTIVATE(ruleId), {});
  }

  archiveRule(ruleId: string): Observable<RuleFull> {
    return this.http.post<RuleFull>(RULE_POST_ARCHIVE(ruleId), {});
  }

  estimateRuleUsage(
    connectorId: string,
    objectTypeId: number,
    query: string,
    timeStart: number,
    timeEnd: number,
    ruleId?: string,
  ): Observable<RuleEstimationResponse> {
    const params = {
      connector_id: connectorId,
      object_type_id: objectTypeId,
      settings: {
        boolean_query: query,
        time_start: timeStart,
        time_end: timeEnd,
      },
      // the version being edited - the log row references it (WAP-1483)
      ...(ruleId ? { rule_id: ruleId } : {}),
    };
    return this.http.post<RuleEstimationResponse>(RULE_POST_ESTIMATE, params);
  }

  createRule(
    connectorId: string,
    ruleTypeId: number,
    title: string,
    settings: RuleSettings,
    estimationFingerprintId?: string,
  ): Observable<RuleFull> {
    const params = {
      connector_id: connectorId,
      object_type_id: ruleTypeId,
      title,
      settings,
      ...(estimationFingerprintId
        ? { estimation_fingerprint_id: estimationFingerprintId }
        : {}),
    };

    return this.http.put<any>(RULE_PUT, params);
  }

  /** Historical rule executions, latest first (SEN-5078). */
  getRuleExecutions(ruleId: string): Observable<RuleExecution[]> {
    return this.http.get<RuleExecution[]>(RULE_GET_EXECUTIONS(ruleId));
  }

  /**
   * One page of logged API calls for the ag-grid Server-Side Row Model: the
   * raw ag-grid request (startRow/endRow/sortModel/filterModel) is the POST
   * body verbatim; the optional execution scope rides on the query string.
   */
  searchRuleApiCalls(
    ruleId: string,
    request: IServerSideGetRowsRequest,
    executionId?: string,
  ): Observable<RuleApiCallsResponse> {
    return this.http.post<RuleApiCallsResponse>(
      RULE_POST_API_CALLS(ruleId),
      request,
      { params: toQueryParams({ execution_id: executionId }) },
    );
  }

  /** One api-call row with its raw request/response payloads. */
  getRuleApiCallDetail(
    ruleId: string,
    callId: number,
  ): Observable<RuleApiCallDetailResponse> {
    return this.http.get<RuleApiCallDetailResponse>(
      RULE_GET_API_CALL_DETAIL(ruleId),
      { params: toQueryParams({ call_id: callId }) },
    );
  }

  /** One logged estimation of the rule, by its fingerprint (WAP-1483). */
  getRuleEstimation(
    ruleId: string,
    fingerprintId: string,
  ): Observable<RuleEstimationDetailResponse> {
    return this.http.get<RuleEstimationDetailResponse>(
      RULE_GET_ESTIMATION(ruleId),
      { params: toQueryParams({ fingerprint_id: fingerprintId }) },
    );
  }

  /** Queue a (re)run of the post-execution analysis worker. */
  analyzeRule(
    ruleId: string,
    executionId: string,
  ): Observable<RuleAnalyzeResponse> {
    return this.http.post<RuleAnalyzeResponse>(RULE_POST_ANALYZE(ruleId), {
      executionId,
    });
  }
}
